import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { LanguageMyReportList, LanguagePrompt } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { DefaultReportListResponse } from './Types'
import { MenuOption, ReactState } from '../../Common/Types/Types'
import { displayError } from '../../Common/Utils/Messages'
import {
    defaultReportCollectionNameURL,
    defaultReportCreationURL,
    defaultReportSetCreationURL,
    moveReportsToBinMURL
} from '../../../NonReact/constants/ApiConstants'
import PromptOverrideExistingReportsMessage from './PromptOverrideExistingReportsMessage'
import { PromptContext } from '../../Common/Contexts'
import SubMenuEntry from '../../Common/Components/OptionsMenu/Components/SubMenuEntry'
import { announceOptionsMenuClose } from '../../../NonReact/common/RxJS/RowMenuState'
import { PromptDisplayType } from '../../Common/Contexts/PromptContext'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { EventNames } from '../../../NonReact/constants/EventNames'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

/**
 * Component properties type.
 */
type Props = {
    isReportExist: boolean
    initialiseReportListData: () => Promise<void>
}

/**
 * Default report menu component to return the list of default reports.
 * @param isReportExist
 * @param initialiseReportListData Report list data initialisation method.
 * @returns
 */
const DefaultReportMenu: React.FC<Props> = ({ isReportExist, initialiseReportListData }: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    const [defaultReportsData, setDefaultReportsData]: ReactState<DefaultReportListResponse[]> = useState<
        DefaultReportListResponse[]
    >([])
    const { displayPrompt }: PromptDisplayType = useContext(PromptContext)
    language.loadBundle('LanguagePrompt')
    language.loadBundle('LanguageMyReportList')

    /** Method to close menu when mouse down click happen.
     *
     * @param e click event
     */
    const closeMenuEvent = (e: Event): void => {
        if (!e.cancelBubble) {
            announceOptionsMenuClose()
            document.removeEventListener(EventNames.MOUSEDOWN, closeMenuEvent)
        }
    }

    /**
     * Retrieving the list of default reports.
     */
    useEffect((): void => {
        requestGetJSON<DefaultReportListResponse[]>(defaultReportCollectionNameURL)
            .then((response: JSONResponse<DefaultReportListResponse[]>): void => {
                if (response) {
                    setDefaultReportsData(response)
                }
            })
            .catch(displayError)
    }, [])

    /**
     * To create the report set for the given collection name.
     * @param event Click event.
     * @param rptListItem
     */
    const createReportSet = (e: Event, collectionName: string): void => {
        const defaultReportCollectionName = collectionName.replace(/\s/g, '_')
        requestJSON<void>(defaultReportSetCreationURL(defaultReportCollectionName), {
            method: RESTMethods.POST
        })
            .then((): void => {
                initialiseReportListData()
                document.addEventListener(EventNames.MOUSEDOWN, closeMenuEvent)
            })
            .catch(displayError)
        closeMenuEvent(e)
    }

    /**
     * To create the default report for the given collection name and default report name.
     * @param collectionName Defaut report collection name.
     * @param defaultReportName Default report name.
     */
    const createDefaultReport = (collectionName: string, defaultReportName: string): void => {
        requestJSON<void>(defaultReportCreationURL(collectionName, defaultReportName), {
            method: RESTMethods.POST
        })
            .then((): void => {
                initialiseReportListData()
            })
            .catch(displayError)
    }

    /**
     * To move the existing reports to the recycle bin.
     * @param moveToBin Flag to ensure reports needs to be moved to bin or not.
     * @param collectionName Defaut report collection name.
     */
    const moveReportstoBinBeforeCreatingReportSet = (e: Event, moveToBin: boolean, collectionName: string): void => {
        requestJSON<void>(moveReportsToBinMURL(moveToBin), {
            method: RESTMethods.POST
        })
            .then((): void => {
                createReportSet(e, collectionName)
            })
            .catch(displayError)
    }

    /**
     * * Prompt popup warning before moving the reports to bin.
     * @param collectionName Defaut report collection name.
     */
    const displayPromptOnMoveToBinReport = (e: Event, collectionName: string): void => {
        displayPrompt(
            <PromptOverrideExistingReportsMessage />,
            language?.get(LanguagePrompt.LanguagePrompt_PromptConfirm) ?? EMPTY_STRING,
            language?.get(LanguagePrompt.LanguagePrompt_PromptCancel) ?? EMPTY_STRING,
            (messageChecked?: boolean) => {
                if (messageChecked) {
                    moveReportstoBinBeforeCreatingReportSet(e, messageChecked, collectionName)
                } else {
                    createReportSet(e, collectionName)
                }
            },
            undefined,
            undefined,
            language.get(LanguageMyReportList.ReportlistOverrideExistingReport)
        )
        closeMenuEvent(e)
    }

    return (
        <>
            {defaultReportsData &&
                defaultReportsData.map((defaultReportObj: DefaultReportListResponse): ReactElement => {
                    const collectionName: string = defaultReportObj.CollectionName.replace(/_/g, ' ')
                    const defaultReportsList: string[] = defaultReportObj.DefaultReports

                    return (
                        <SubMenuEntry
                            key={collectionName}
                            menuOption={{
                                // Collection name manu
                                isVisible: true,
                                isEnabled: true,
                                title: collectionName,
                                tooltip: collectionName,
                                submenu: defaultReportsList.map(
                                    (reportName: string): MenuOption => ({
                                        // default report menu
                                        isVisible: true,
                                        isEnabled: true,
                                        title: reportName,
                                        tooltip: reportName,
                                        onClick: (): void => {
                                            createDefaultReport(defaultReportObj.CollectionName, reportName)
                                        }
                                    })
                                ),
                                onClick: (e: React.MouseEvent<Element, MouseEvent>): void => {
                                    if (isReportExist) {
                                        displayPromptOnMoveToBinReport(e.nativeEvent, defaultReportObj.CollectionName)
                                    } else {
                                        createReportSet(e.nativeEvent, defaultReportObj.CollectionName)
                                    }
                                }
                            }}
                        />
                    )
                })}
        </>
    )
}

export default DefaultReportMenu
